import { Link } from "react-router-dom";
import { FileText, Car, Wallet, ClipboardCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHead, BtnOrange, BtnGhost } from "@/components/bits";
import FAQ from "@/components/FAQ";
import { usePreRegister } from "@/components/PreRegisterModal";
import { IMAGES, FAQ_MOTORISTAS } from "@/data/site";

const STEPS = [
  { t: "Informações pessoais", d: "O motorista realiza seu cadastro e fornece os dados pessoais solicitados." },
  { t: "Documentação", d: "Envio de CNH com EAR e demais documentos obrigatórios." },
  { t: "Informações do veículo", d: "Cadastro do veículo e envio dos documentos necessários." },
  {
    t: "Cadastro financeiro",
    d: "Após concluir as etapas pessoais e do veículo, o motorista realiza o cadastro financeiro exigido para recebimentos, conforme a integração habilitada.",
  },
  {
    t: "Análise e aprovação",
    d: "A plataforma analisa as informações e determina a situação cadastral.",
  },
];

const CATS = [
  { name: "Básico", rule: "Motoristas enquadrados na categoria Básico recebem solicitações Básico." },
  { name: "Select", rule: "Motoristas enquadrados em Select podem receber solicitações Select e Básico." },
  { name: "Premium", rule: "Motoristas enquadrados em Premium podem receber solicitações Premium, Select e Básico." },
];

const COMERCIAL_ITEMS = [
  "Mensalidades",
  "Faixas de faturamento",
  "Eventuais períodos gratuitos ou promocionais",
  "Vencimento",
  "Condições de suspensão ou reativação",
  "Taxas financeiras de terceiros, quando aplicáveis",
];

export default function Motoristas() {
  const { openPreRegister } = usePreRegister();

  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Seja um motorista parceiro"
        title="Seja um motorista parceiro 360Taxi."
        lead="Faça parte de uma plataforma de mobilidade que valoriza organização, autonomia, qualidade no atendimento e transparência nas condições de utilização."
        image={IMAGES.driverNav}
        imageAlt="Motorista parceiro em ambiente urbano"
      />

      <section className="mx-auto max-w-8xl px-4 py-20 sm:px-6 md:py-24 lg:px-8" data-testid="cadastro-motoristas">
        <Reveal>
          <SectionHead eyebrow="Como funciona o cadastro" title="Cinco etapas até ficar online." />
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-5">
          {STEPS.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.07} className="h-full">
              <div className="flex h-full flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <span className="font-mono text-4xl font-bold text-brand-orange/25">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display text-lg font-bold text-slate-900">{s.t}</h3>
                <p className="text-sm leading-relaxed text-slate-600">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-8 rounded-2xl border-l-4 border-brand-orange bg-slate-50 px-6 py-5 text-base font-semibold text-slate-800">
            Somente motoristas aprovados e com os requisitos de acesso regularizados poderão ficar online e receber
            solicitações.
          </p>
        </Reveal>
      </section>

      <section className="bg-slate-50 py-20 md:py-24" data-testid="requisitos-motoristas">
        <div className="mx-auto grid max-w-8xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <SectionHead
              eyebrow="Requisitos gerais"
              title="O que é necessário para atuar."
            />
            <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
              Para atuar no 360Taxi, o motorista deverá atender aos requisitos de habilitação, documentação, veículo,
              seguro, segurança e demais condições exigidas pela legislação e pelas regras da plataforma.
            </p>
            <div className="mt-6 flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6">
              <Car className="h-8 w-8 shrink-0 text-brand-orange" />
              <p className="text-base leading-relaxed text-slate-700">
                O veículo deve respeitar a regra vigente de idade máxima de 10 anos, além dos demais requisitos
                aplicáveis.
              </p>
            </div>
            <p className="mt-6 text-sm text-slate-500">
              A plataforma não divulga publicamente marcas, modelos ou listas internas de elegibilidade de veículos.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 lg:grid-cols-1">
              {CATS.map((c) => (
                <div key={c.name} className="rounded-2xl border border-slate-200 bg-white p-6">
                  <h3 className="font-display text-lg font-bold text-slate-900">{c.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.rule}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="mx-auto mt-8 max-w-8xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-sm text-slate-500">
              A classificação é feita pela plataforma conforme análise e requisitos internos.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-brand-obsidian py-20 md:py-24" data-testid="mensalidades-section">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0 h-80 w-80 rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, rgba(254,115,13,0.5) 0%, transparent 65%)" }}
        />
        <div className="relative mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHead
              dark
              eyebrow="Mensalidade 360Taxi"
              title="Quanto custa dirigir com o 360Taxi?"
              lead="Você dirige, recebe o valor das suas corridas e paga uma mensalidade conforme seu faturamento. Sem comissão percentual da plataforma sobre cada corrida."
            />
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-10 overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur">
              <table className="hidden w-full text-left md:table" data-testid="mensalidades-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-7 py-5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Faturamento mensal do motorista
                    </th>
                    <th className="px-7 py-5 text-right font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Mensalidade 360Taxi
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { faixa: "Até R$ 2.000,00", valor: "R$ 159,90" },
                    { faixa: "De R$ 2.000,01 até R$ 3.000,00", valor: "R$ 219,90" },
                    { faixa: "De R$ 3.000,01 até R$ 4.000,00", valor: "R$ 300,00" },
                    { faixa: "Acima de R$ 4.000,00", valor: "R$ 369,90" },
                  ].map((r, i) => (
                    <tr key={r.faixa} className={`border-b border-white/10 last:border-0 ${i % 2 ? "bg-white/[0.03]" : ""}`}>
                      <td className="px-7 py-5 text-base text-white" data-testid={`mensalidade-faixa-${i}`}>
                        {r.faixa}
                      </td>
                      <td className="px-7 py-5 text-right font-display text-2xl font-extrabold text-brand-orange" data-testid={`mensalidade-valor-${i}`}>
                        {r.valor}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="space-y-3 p-4 md:hidden">
                {[
                  { faixa: "Até R$ 2.000,00", valor: "R$ 159,90" },
                  { faixa: "De R$ 2.000,01 até R$ 3.000,00", valor: "R$ 219,90" },
                  { faixa: "De R$ 3.000,01 até R$ 4.000,00", valor: "R$ 300,00" },
                  { faixa: "Acima de R$ 4.000,00", valor: "R$ 369,90" },
                ].map((r, i) => (
                  <div
                    key={r.faixa}
                    data-testid={`mensalidade-card-${i}`}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4"
                  >
                    <span className="text-sm leading-snug text-white">{r.faixa}</span>
                    <span className="shrink-0 font-display text-xl font-extrabold text-brand-orange">{r.valor}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-10 grid gap-6 lg:grid-cols-2" data-testid="mensalidades-como-funciona">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-7">
                <h3 className="font-display text-xl font-bold text-white">Como funciona?</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                  O valor da mensalidade é determinado conforme a faixa de faturamento mensal do motorista no
                  360Taxi.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                  Diferentemente dos modelos que cobram comissão percentual sobre cada corrida, o 360Taxi utiliza
                  uma mensalidade variável por faixa de faturamento.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                  O motorista poderá acompanhar suas informações financeiras diretamente no aplicativo.
                </p>
              </div>
              <div className="rounded-2xl border border-brand-orange/30 bg-brand-orange/10 p-7">
                <h3 className="font-display text-lg font-bold text-brand-amber">Informação importante</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                  O faturamento considerado para o enquadramento inclui os valores das corridas realizadas na
                  plataforma, independentemente de terem sido pagas por Pix, cartão ou dinheiro, conforme as regras
                  de apuração vigentes.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                  Eventuais tarifas de processamento de pagamentos ou transferências financeiras, quando
                  aplicáveis, são distintas da mensalidade da plataforma.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <p className="mt-8 text-xs leading-relaxed text-slate-500" data-testid="mensalidades-nota">
              Valores referentes à mensalidade da plataforma, sujeitos às condições comerciais e contratuais
              vigentes. O motorista deverá consultar no aplicativo as condições aplicáveis à sua conta.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-8xl px-4 pb-4 sm:px-6 lg:px-8" data-testid="seguro-obrigatorio">
        <Reveal>
          <SectionHead
            eyebrow="Seguro obrigatório"
            title="Seguro do veículo para atuar no 360Taxi."
            lead="Para ser aprovado no cadastro, o motorista deve comprovar seguro do veículo conforme os requisitos da plataforma, contratando a apólice do seguro parceiro ou apresentando apólice própria válida."
          />
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8">
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Seguradora parceira
              </p>
              <p className="mt-2 font-display text-2xl font-bold text-slate-900">MBM — Seguro APP</p>
              <p className="mt-5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Valor anual de referência
              </p>
              <p className="mt-2 font-display text-3xl font-extrabold text-brand-orange" data-testid="seguro-valor-anual">
                R$ 129,66
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Sujeito à confirmação no momento da contratação.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-8">
              <h3 className="font-display text-lg font-bold text-slate-900">Coberturas</h3>
              <ul className="mt-4 space-y-2.5" data-testid="seguro-coberturas">
                <li className="flex items-start gap-3 text-sm text-slate-700 sm:text-base">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                  Morte acidental: R$ 100.000,00
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-700 sm:text-base">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                  Invalidez permanente total ou parcial por acidente: até R$ 100.000,00
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-700 sm:text-base">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                  Despesas médicas, hospitalares e odontológicas: até R$ 15.000,00 por reembolso
                </li>
              </ul>
              <h3 className="mt-8 font-display text-lg font-bold text-slate-900">Como funciona no cadastro</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                O motorista poderá contratar a apólice por meio do link ou atendimento da seguradora
                disponibilizado no aplicativo, ou anexar uma apólice própria válida que atenda aos requisitos
                exigidos. Após o envio, a documentação passa por análise e aprovação.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="grain relative overflow-hidden bg-brand-obsidian py-20 md:py-24" data-testid="modelo-comercial">
        <div className="relative mx-auto grid max-w-8xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <SectionHead
              dark
              eyebrow="Modelo comercial"
              title="Mais autonomia para quem dirige."
              lead="O 360Taxi utiliza um modelo comercial baseado em mensalidade, sem comissão percentual da plataforma sobre cada corrida."
            />
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300">
              A tabela oficial de mensalidades por faixa de faturamento está publicada nesta página. As demais
              condições comerciais aplicáveis continuarão sendo informadas claramente ao motorista.
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {COMERCIAL_ITEMS.map((i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                  {i}
                </li>
              ))}
            </ul>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
              Valores não confirmados não são publicados neste site
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur sm:p-10">
              <Wallet className="h-10 w-10 text-brand-orange" />
              <h3 className="mt-5 font-display text-2xl font-bold text-white">
                Acompanhe suas informações financeiras.
              </h3>
              <p className="mt-3 text-base leading-relaxed text-slate-300">
                O motorista poderá acompanhar seus recebimentos e movimentações pelos recursos financeiros
                disponibilizados na plataforma.
              </p>
              <ul className="mt-5 space-y-2.5 text-sm leading-relaxed text-slate-400">
                <li>• Pagamentos eletrônicos e transferências sujeitos à integração financeira habilitada.</li>
                <li>• Corridas pagas em dinheiro poderão ser permitidas conforme as configurações disponíveis.</li>
              </ul>
              <p className="mt-5 text-xs leading-relaxed text-slate-500">
                Não são prometidos recebimento instantâneo, saques sem taxas ou carteira operacional antes da
                confirmação técnica e contratual.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 md:py-24 lg:px-8" data-testid="faq-motoristas">
        <Reveal>
          <SectionHead eyebrow="Perguntas frequentes" title="Dúvidas de motoristas parceiros." />
        </Reveal>
        <Reveal delay={0.1}>
          <FAQ items={FAQ_MOTORISTAS} idPrefix="faq-motoristas" />
        </Reveal>
      </section>

      <section className="mx-auto max-w-8xl px-4 pb-24 pt-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-center gap-6 rounded-3xl bg-brand-obsidian px-6 py-14 text-center sm:px-12">
            <ClipboardCheck className="h-10 w-10 text-brand-orange" />
            <h2 className="max-w-xl font-display text-2xl font-extrabold text-white sm:text-3xl">
              Quer receber informações sobre o cadastro de motoristas?
            </h2>
            <div className="flex flex-col gap-3 sm:flex-row">
              <BtnOrange
                data-testid="motoristas-cta-pre-cadastro"
                onClick={() => openPreRegister({ interesse: "Motorista 360Taxi", origem: "motoristas" })}
              >
                <FileText className="h-4 w-4" /> Quero ser avisado
              </BtnOrange>
              <BtnGhost as={Link} to="/suporte" className="!bg-transparent">
                Falar com o suporte
              </BtnGhost>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
